import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import centroid from "@turf/centroid";
import along from "@turf/along";
import calculate_length from "@turf/length";

/*COMPONENT*/
import VirtualTableRow from "./VirtualTableRow";
import FLOAT_MENU from "../common_trivial/FLOAT_MENU";
import Modal from "../common_modal/Modal";

//use float
import C_E_SELECT from "../c_edit/C_E_SELECT";
import C_E_MULTI from "../c_edit/C_E_MULTI";
import C_E_DOC from "../c_edit/C_E_DOC";
import C_U_IMG from "../c_edit/C_U_IMG";

//use modal
import C_E_CITY from "../c_edit/C_E_CITY";
import C_E_ADDRESS from "../c_edit/C_E_ADDRESS";
import C_E_USER from "../c_edit/C_E_USER";
import C_E_LATLNG from "../c_edit/C_E_LATLNG";
import C_E_TABLE from "../c_edit/C_E_TABLE";

/*REDUX*/
import {
  editProperties,
  editPropertiesV2,
  deleteFeature,
  set_value_layer,
} from "../../App/actions/layerActions";
import { set_value_properties } from "../../App/actions/propertiesActions";
import { status_action } from "../../App/actions/mapActions";
import {
  edit_child_cell,
  delete_child_cell,
} from "../../App/actions/crudNestedTable";

/*FUNCTION*/
import { packThePropertiesDataToUseApiEdit } from "../../App/validation/convert_data";

/*DATA*/
import dict from "../../Data/dict.json";

export default function TableBody({
  data,
  fullData = [],
  visibleFields = [],
  totalWidth = 1000,
  fields,
  onLocalDataUpdate,
  editable = true,
  rowHeight = 35,
  offsetY = 0,
  totalHeight = 0,
  containerRef,
  isNested,
}) {
  const cellRefs = useRef({});
  const targetFocusRef = useRef({ row: null, col: null });
  const prevSelectedKeyRef = useRef(null);
  const isTableSelectionRef = useRef(false);
  const singleClickTimerRef = useRef(null);

  // FIX: Refs to debounce rapid click storms on the same cell
  const clickTimerRef = useRef(null);
  const lastClickCellKeyRef = useRef(null);

  const dispatch = useDispatch();
  const layer_state = useSelector((state) => state.layer);
  const auth_state = useSelector((state) => state.auth);
  const properties_state = useSelector((state) => state.properties);

  const mode_map = properties_state?.mode_map;
  const isViewer = mode_map === "viewer";

  const global_cell_editor = properties_state?.global_cell_editor;

  const [editing, setEditing] = useState({
    rowId: null,
    key: null,
    tempValue: "",
  });
  const [selectedRow, setSelectedRow] = useState({ row: null, col: null });
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [selected_feature, setSelectedFeature] = useState(null);

  // Modals
  const [modalDeleteFeature, setModalDeleteFeature] = useState(false);
  const [cityModalConfig, setCityModalConfig] = useState(null);
  const [addressModalConfig, setAddressModalConfig] = useState(null);
  const [userModalConfig, setUserModalConfig] = useState(null);
  const [latLngModalConfig, setLatLngModalConfig] = useState(null);
  const [tableModalConfig, setTableModalConfig] = useState(null);

  const startIndex = Math.floor(offsetY / rowHeight);
  const language = localStorage?.language || auth_state?.language || "id";
  const isMenuOpen = Boolean(anchorEl) || Boolean(anchorPosition);

  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      if (singleClickTimerRef.current)
        clearTimeout(singleClickTimerRef.current); // add this
    };
  }, []);

  useEffect(() => {
    const selectedKey =
      layer_state?.feature_object_selected?.key ||
      properties_state?.feature_object_selected?.key;
    if (!selectedKey || !containerRef?.current) {
      prevSelectedKeyRef.current = selectedKey;
      return;
    }
    if (selectedKey !== prevSelectedKeyRef.current) {
      prevSelectedKeyRef.current = selectedKey;
      if (isTableSelectionRef.current) {
        isTableSelectionRef.current = false;
        return;
      }
      setSelectedRow({ row: null, col: null });
      targetFocusRef.current = { row: null, col: null };
      setEditing({ rowId: null, key: null, tempValue: "" });
      if (document.activeElement && document.activeElement.tagName !== "BODY") {
        document.activeElement.blur();
      }
      const container = containerRef.current;
      let absoluteIndex = -1;
      if (fullData && fullData.length > 0) {
        absoluteIndex = fullData.findIndex(
          (f) => f.key === selectedKey || f.id === selectedKey,
        );
      } else {
        const indexInData = data.findIndex(
          (f) => f.key === selectedKey || f.id === selectedKey,
        );
        if (indexInData !== -1) absoluteIndex = startIndex + indexInData;
      }
      if (absoluteIndex !== -1) {
        const targetTop = absoluteIndex * rowHeight;
        const centerTop =
          targetTop - container.clientHeight / 2 + rowHeight / 2;
        container.scrollTo({ top: Math.max(0, centerTop), behavior: "smooth" });
      }
    }
  }, [
    layer_state?.feature_object_selected?.key,
    properties_state?.feature_object_selected?.key,
    containerRef,
    rowHeight,
    startIndex,
    data,
    fullData,
  ]);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container || selectedRow.row === null || selectedRow.col === null)
      return;
    if (
      targetFocusRef.current.row !== selectedRow.row ||
      targetFocusRef.current.col !== selectedRow.col
    )
      return;
    const cellEl = cellRefs.current[selectedRow.row]?.[selectedRow.col];
    if (cellEl) {
      cellEl.focus({ preventScroll: true });
      const cellRect = cellEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const numberColumnWidth = 70;
      const leftBoundary =
        selectedRow.col === 0
          ? containerRect.left + numberColumnWidth
          : containerRect.left;
      let targetScrollLeft = container.scrollLeft;
      let targetScrollTop = container.scrollTop;
      if (cellRect.right > containerRect.right)
        targetScrollLeft += cellRect.right - containerRect.right;
      else if (cellRect.left < leftBoundary)
        targetScrollLeft -= leftBoundary - cellRect.left;
      const topBoundary = containerRect.top + rowHeight;
      if (cellRect.bottom > containerRect.bottom)
        targetScrollTop += cellRect.bottom - containerRect.bottom;
      else if (cellRect.top < topBoundary)
        targetScrollTop -= topBoundary - cellRect.top;
      if (
        targetScrollLeft !== container.scrollLeft ||
        targetScrollTop !== container.scrollTop
      ) {
        container.scrollTo({
          left: targetScrollLeft,
          top: targetScrollTop,
          behavior: "smooth",
        });
      }
      targetFocusRef.current = { row: null, col: null };
    } else {
      const maxRenderedRow = startIndex + data.length - 1;
      let targetScrollTop = container.scrollTop;
      if (selectedRow.row > maxRenderedRow)
        targetScrollTop += rowHeight * (selectedRow.row - maxRenderedRow);
      else if (selectedRow.row < startIndex + 1)
        targetScrollTop -= rowHeight * (startIndex + 1 - selectedRow.row);
      container.scrollTo({ top: targetScrollTop, behavior: "smooth" });
    }
  }, [selectedRow, containerRef, rowHeight, startIndex, data]);

  const selectFeatureInRedux = useCallback(
    (feature) => {
      if (isNested) return;
      if (!feature || !feature.key) return;
      const currentKey =
        layer_state?.feature_object_selected?.key ||
        properties_state?.feature_object_selected?.key;
      if (currentKey !== feature.key) {
        isTableSelectionRef.current = true;
        dispatch(
          set_value_properties({
            key: "feature_key_selected",
            value: feature.key,
          }),
        );
        dispatch(
          set_value_layer({ key: "feature_object_selected", value: feature }),
        );
      }
    },
    [
      isNested,
      dispatch,
      layer_state?.feature_object_selected?.key,
      properties_state?.feature_object_selected?.key,
    ],
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setAnchorPosition(null);
  }, []);

  const on_fly_feature = useCallback(
    (feature_to_fly) => {
      dispatch(set_value_properties({ key: "popupInfo", value: null }));
      const old_feature_key_selected =
        layer_state?.feature_object_selected?.key ||
        properties_state?.feature_object_selected?.key;
      const feature_key_selected = feature_to_fly?.key;
      if (old_feature_key_selected === feature_key_selected) {
        dispatch(
          set_value_properties({ key: "feature_key_selected", value: "" }),
        );
        dispatch(set_value_layer({ key: "popup_dom", value: null }));
        dispatch(
          set_value_layer({ key: "feature_object_selected", value: null }),
        );
      } else {
        dispatch(
          set_value_properties({
            key: "feature_key_selected",
            value: feature_key_selected,
          }),
        );
        const geo_layer_list = layer_state.geo_layer_list || [];
        const layer_id = layer_state.layer_id || "";
        const geo_layer = geo_layer_list.find(
          (d) => d.geo_layer._id === layer_id,
        )?.geo_layer;
        const feature = geo_layer?.geojson?.features?.find(
          (d) => d?.key === feature_key_selected,
        );
        dispatch(
          set_value_layer({ key: "feature_object_selected", value: feature }),
        );
        dispatch(status_action());
        dispatch(
          set_value_properties({ key: "modal_pop_up_layer", value: true }),
        );
      }
    },
    [properties_state, layer_state, dispatch],
  );

  const on_popup_editor = useCallback(
    (feature_to_fly) => {
      on_fly_feature(feature_to_fly);

      const { geo_layer_list, layer_id } = layer_state;
      const geo_layer = geo_layer_list?.find(
        (item) => item?.geo_layer?._id === layer_id,
      )?.geo_layer;

      const geometry = feature_to_fly?.geometry;
      const type = geometry?.type;
      let longitude, latitude;
      if (type === "Point") {
        [longitude, latitude] = geometry.coordinates;
      } else if (type === "LineString") {
        const length_km = calculate_length(feature_to_fly, {
          units: "kilometers",
        });
        const center = along(feature_to_fly, length_km / 2, {
          units: "kilometers",
        });
        [longitude, latitude] = center.geometry.coordinates;
      } else {
        const center = centroid({
          type: "FeatureCollection",
          features: [feature_to_fly],
        });
        [longitude, latitude] = center.geometry.coordinates;
      }

      const type_2 = geo_layer?.side_table_object?.type_2;
      const fields = geo_layer?.fields || [];

      dispatch(
        set_value_layer({ key: "modal_edit_feature_properties", value: true }),
      );
      dispatch(set_value_layer({ key: "latitude_selected", value: latitude }));
      dispatch(
        set_value_layer({ key: "longitude_selected", value: longitude }),
      );
      dispatch(set_value_layer({ key: "fields_selected", value: fields }));
      dispatch(
        set_value_layer({
          key: "properties_selected",
          value: feature_to_fly.properties,
        }),
      );
      dispatch(set_value_layer({ key: "type_2_selected", value: type_2 }));
      dispatch(
        set_value_layer({
          key: "feature_key_selected",
          value: feature_to_fly.properties?.key,
        }),
      );
      dispatch(
        set_value_layer({ key: "geo_layer_selected", value: geo_layer }),
      );
      dispatch(
        set_value_layer({ key: "layer_id_popup", value: geo_layer?._id }),
      );
    },
    [on_fly_feature, layer_state, dispatch],
  );

  const handleDeleteFeature = useCallback(
    (e) => {
      e.preventDefault();
      if (!selected_feature) return;
      if (!isNested) {
        dispatch(
          deleteFeature({
            feature_key: selected_feature.key,
            geo_layer_id: layer_state.layer_id,
          }),
        );
      } else {
        dispatch(
          delete_child_cell({
            geo_layer_id: layer_state.layer_id,
            feature_key: properties_state.feature_key_nested_table,
            child_uuid: selected_feature.child_uuid,
          }),
        );
      }
      setModalDeleteFeature(false);
    },
    [
      isNested,
      selected_feature,
      layer_state.layer_id,
      dispatch,
      properties_state.feature_key_nested_table,
    ],
  );

  const startEdit = useCallback(
    (row, field) => {
      if (
        !editable ||
        !onLocalDataUpdate ||
        isViewer ||
        field.type === "math_operators" ||
        field.type === "auto_generate_user_name" ||
        field.type === "edited_by"
      )
        return;
      setEditing({
        rowId: row.id,
        key: field.key,
        tempValue: row?.properties?.[field.key] ?? "",
      });
    },
    [editable, onLocalDataUpdate, isViewer],
  );

  const stopEdit = useCallback(() => {
    setEditing({ rowId: null, key: null, tempValue: "" });
  }, []);

  const closeGlobalEditor = useCallback(() => {
    dispatch(set_value_properties({ key: "global_cell_editor", value: null }));
    stopEdit();
  }, [dispatch, stopEdit]);

  const handleChange = useCallback((e) => {
    setEditing((prev) => ({ ...prev, tempValue: e.target.value }));
  }, []);

  const handleSaveData = useCallback(
    (rowId, fieldKey, newValue, oldValue) => {
      if (isViewer || String(oldValue ?? "") === String(newValue ?? "")) return;
      if (onLocalDataUpdate) onLocalDataUpdate(rowId, fieldKey, newValue);
      const row = data.find((r) => r.id === rowId);
      if (!row) return;
      const { layer_id: target_layer_id, geo_layer_list } = layer_state;
      const geo_layer = geo_layer_list?.find(
        (item) => item.geo_layer._id === target_layer_id,
      )?.geo_layer;
      if (geo_layer) {
        if (isNested) {
          dispatch(
            edit_child_cell({
              geo_layer_id: target_layer_id,
              feature_key: properties_state.feature_key_nested_table,
              child_uuid: row.child_uuid,
              properties_key: fieldKey,
              properties_value: newValue,
            }),
          );
        } else {
          const editFn =
            geo_layer.architecture === "v2" ? editPropertiesV2 : editProperties;
          const updated_at_key = geo_layer.fields?.find(
            (item) =>
              item?.parent_uuid === undefined && item?.type === "last_edited",
          )?.key;
          const updated_by_key = geo_layer.fields?.find(
            (item) =>
              item?.parent_uuid === undefined && item?.type === "edited_by",
          )?.key;
          const params = {
            id: rowId,
            field: fieldKey,
            row: {
              ...row,
              properties: { ...row.properties, [fieldKey]: newValue },
            },
            value: newValue,
          };
          const body = packThePropertiesDataToUseApiEdit(
            target_layer_id,
            params,
          );
          if (updated_at_key) {
            body.updated_at_properties_key = updated_at_key;
            body.updated_at_properties_value = new Date();
          }
          if (updated_by_key) {
            body.updated_by_properties_key = updated_by_key;
            body.updated_by_properties_value = auth_state.user?.name;
          }
          dispatch(editFn(body));
        }
      }
    },
    [
      isViewer,
      onLocalDataUpdate,
      data,
      layer_state,
      isNested,
      dispatch,
      properties_state.feature_key_nested_table,
      auth_state.user?.name,
    ],
  );

  const handleBlur = useCallback(
    (rowId, key, oldValue) => {
      if (editing.rowId === rowId && editing.key === key)
        handleSaveData(rowId, key, editing.tempValue, oldValue);
      setEditing({ rowId: null, key: null, tempValue: "" });
    },
    [editing, handleSaveData],
  );

  const handleKeyDown = useCallback(
    (e, rowId, key, oldValue) => {
      if (e.key === "Enter" || e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (e.key === "Enter") handleBlur(rowId, key, oldValue);
        else stopEdit();
        setTimeout(
          () =>
            cellRefs.current[selectedRow.row]?.[selectedRow.col]?.focus({
              preventScroll: true,
            }),
          0,
        );
      }
    },
    [handleBlur, stopEdit, selectedRow],
  );

  const setCellRef = useCallback(
    (absoluteRow, cIndex) => (el) => {
      if (!cellRefs.current[absoluteRow]) cellRefs.current[absoluteRow] = {};
      cellRefs.current[absoluteRow][cIndex] = el;
    },
    [],
  );

  const onExpandClick = useCallback((e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedFeature(row);
  }, []);

  const onContextMenu = useCallback((e, row) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedFeature(row);
    setAnchorPosition({ top: e.clientY, left: e.clientX });
  }, []);

  // FIX: Memoized helper — avoids re-creating on every render
  const getCellDataFromEvent = useCallback(
    (e) => {
      const cellEl = e.target.closest('[role="cell"]');
      if (!cellEl) return null;
      const absRow = parseInt(cellEl.dataset.absRow, 10);
      const colIdx = parseInt(cellEl.dataset.colIdx, 10);
      const rowId = cellEl.dataset.rowId;
      const fieldKey = cellEl.dataset.fieldKey;
      if (isNaN(absRow) || isNaN(colIdx) || !rowId || !fieldKey) return null;
      const formattedRowId = isNaN(Number(rowId)) ? rowId : Number(rowId);
      const field = fields.find((f) => f.key === fieldKey) || fields[colIdx];
      let row = data.find((r) => r.id === formattedRowId);
      if (!row && fullData) {
        row = fullData.find((r) => r.id === formattedRowId);
      }
      return { cellEl, absRow, colIdx, rowId: formattedRowId, field, row };
    },
    [fields, data, fullData],
  );

  // FIX: Extracted shared open-editor logic used by both click and keyboard handlers.
  // Centralising here avoids duplicating the large if/else chain in two places.
  const openEditorForCellData = useCallback(
    (cellData) => {
      if (!cellData?.row || !cellData?.field) return;
      const { row, field, cellEl } = cellData;
      const value = row?.properties?.[field.key];

      if (field.type === "image") {
        if (!isViewer) {
          dispatch(
            set_value_properties({
              key: "global_cell_editor",
              value: { type: "image", row, field, anchorEl: cellEl },
            }),
          );
        }
      } else if (field.type === "document") {
        if (!isViewer) {
          dispatch(
            set_value_properties({
              key: "global_cell_editor",
              value: { type: "document", row, field, anchorEl: cellEl },
            }),
          );
        }
      } else if (field.type === "table") {
        setTableModalConfig({
          row,
          field,
          child_array: row?.child_array,
          isNested,
        });
      } else if (
        ["kelurahan", "kecamatan", "kota", "provinsi"].includes(field.type)
      ) {
        if (!isViewer) {
          setCityModalConfig({ row, field, initialValue: value });
        }
      } else if (["auto_address", "auto_address_poi"].includes(field.type)) {
        if (!isViewer) {
          setAddressModalConfig({ row, field, initialValue: value });
        }
      } else if (["latitude", "longitude"].includes(field.type)) {
        if (!isViewer) {
          setLatLngModalConfig({ row, field, initialValue: value });
        }
      } else if (
        field.type === "auto_generate_user_name" ||
        field.type === "edited_by"
      ) {
        setUserModalConfig({ row, field });
      } else {
        startEdit(row, field);
      }
    },
    [isViewer, isNested, dispatch, startEdit],
  );

  const handleDelegatedClick = useCallback(
    (e) => {
      if (e.detail > 1) return; // double-click handled by onDoubleClick

      const cellData = getCellDataFromEvent(e);
      if (!cellData || !cellData.row || !cellData.field) return;
      e.stopPropagation();

      const isCurrentlyEditing =
        editing.rowId === cellData.rowId && editing.key === cellData.field.key;
      if (isCurrentlyEditing) return;

      // Debounce guard (keeps the existing click-storm protection)
      const cellKey = `${cellData.absRow}-${cellData.colIdx}`;
      if (
        lastClickCellKeyRef.current === cellKey &&
        clickTimerRef.current !== null
      ) {
        return;
      }
      lastClickCellKeyRef.current = cellKey;
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = setTimeout(() => {
        lastClickCellKeyRef.current = null;
        clickTimerRef.current = null;
      }, 300);

      const isAlreadySelected =
        selectedRow.row === cellData.absRow &&
        selectedRow.col === cellData.colIdx;

      if (!isAlreadySelected) {
        if (editing.rowId !== null) stopEdit();
        targetFocusRef.current = { row: cellData.absRow, col: cellData.colIdx };
        setSelectedRow({ row: cellData.absRow, col: cellData.colIdx });
        selectFeatureInRedux(cellData.row);
      } else {
        // FIX: Already selected — schedule editor open after double-click window.
        // If a double-click arrives within 220ms, onDoubleClick will cancel this.
        clearTimeout(singleClickTimerRef.current);
        singleClickTimerRef.current = setTimeout(() => {
          openEditorForCellData(cellData);
          selectFeatureInRedux(cellData.row);
        }, 220);
      }
    },
    [
      getCellDataFromEvent,
      editing.rowId,
      editing.key,
      selectedRow,
      stopEdit,
      selectFeatureInRedux,
      openEditorForCellData,
    ],
  );

  const handleDelegatedDoubleClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      // FIX: Cancel any pending single-click editor-open before double-click takes over
      clearTimeout(singleClickTimerRef.current);

      const cellData = getCellDataFromEvent(e);
      if (!cellData || !cellData.row || !cellData.field) return;
      if (cellData.field.type === "section") return;

      const isAlreadySelected =
        selectedRow.row === cellData.absRow &&
        selectedRow.col === cellData.colIdx;
      if (!isAlreadySelected) {
        targetFocusRef.current = { row: cellData.absRow, col: cellData.colIdx };
        setSelectedRow({ row: cellData.absRow, col: cellData.colIdx });
        selectFeatureInRedux(cellData.row);
      }

      openEditorForCellData(cellData);
      selectFeatureInRedux(cellData.row);
    },
    [
      getCellDataFromEvent,
      selectedRow,
      selectFeatureInRedux,
      openEditorForCellData,
    ],
  );

  const handleDelegatedKeyDown = useCallback(
    (e) => {
      if (editing.rowId) {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          stopEdit();
          setTimeout(
            () =>
              cellRefs.current[selectedRow.row]?.[selectedRow.col]?.focus({
                preventScroll: true,
              }),
            0,
          );
        }
        return;
      }

      const cellData = getCellDataFromEvent(e);
      if (!cellData) return;
      let { absRow: row, colIdx: col } = cellData;

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedRow({ row: null, col: null });
        targetFocusRef.current = { row: null, col: null };
        e.target.blur();
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (
          cellData.row &&
          cellData.field &&
          cellData.field.type !== "section"
        ) {
          // FIX: Delegate to shared helper — no more duplicated if/else chain
          openEditorForCellData(cellData);
          selectFeatureInRedux(cellData.row);
        }
        return;
      }

      if (["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        const totalRows = Math.max(
          Math.ceil(totalHeight / rowHeight),
          startIndex + data.length,
        );
        if (e.key === "ArrowRight" && col < fields.length - 1) col++;
        else if (e.key === "ArrowLeft" && col > 0) col--;
        else if (e.key === "ArrowDown" && row < totalRows - 1) row++;
        else if (e.key === "ArrowUp" && row > 0) row--;
        if (row !== cellData.absRow || col !== cellData.colIdx) {
          targetFocusRef.current = { row, col };
          setSelectedRow({ row, col });
        }
      }
    },
    [
      editing.rowId,
      stopEdit,
      selectedRow,
      getCellDataFromEvent,
      openEditorForCellData,
      selectFeatureInRedux,
      totalHeight,
      rowHeight,
      startIndex,
      data.length,
      fields.length,
    ],
  );

  return (
    <>
      <div
        role="rowgroup"
        className="table_body_wrapper"
        onClick={handleDelegatedClick}
        onDoubleClick={handleDelegatedDoubleClick}
        onKeyDown={handleDelegatedKeyDown}
      >
        {data.map((row, rindex) => {
          const absoluteRow = startIndex + rindex;
          const visualRowNumber = startIndex + rindex + 1;
          const isFeatureSelected =
            row.key &&
            (row.key === layer_state?.feature_object_selected?.key ||
              row.key === properties_state?.feature_object_selected?.key);

          return (
            <VirtualTableRow
              key={row.id || row.key || rindex}
              row={row}
              absoluteRow={absoluteRow}
              visualRowNumber={visualRowNumber}
              visibleFields={visibleFields}
              totalWidth={totalWidth}
              activeCol={
                selectedRow.row === absoluteRow ? selectedRow.col : null
              }
              editingField={editing.rowId === row.id ? editing.key : null}
              tempValue={editing.rowId === row.id ? editing.tempValue : ""}
              isFeatureSelected={isFeatureSelected}
              rowHeight={rowHeight}
              editable={editable}
              isViewer={isViewer}
              onExpandClick={onExpandClick}
              onContextMenu={onContextMenu}
              setCellRef={setCellRef}
              handleChange={handleChange}
              handleBlur={handleBlur}
              handleKeyDown={handleKeyDown}
              stopEdit={stopEdit}
              handleSaveData={handleSaveData}
            />
          );
        })}
      </div>

      <FLOAT_MENU
        id="table-body-menu"
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
        anchorEl={anchorEl}
        anchorPosition={anchorPosition}
      >
        <div>
          {!isNested && (
            <>
              <button
                className="button hover_primary margin_bottom"
                onClick={() => {
                  on_fly_feature(selected_feature);
                  handleMenuClose();
                }}
              >
                Fly To
              </button>
              <br />
              <button
                className="button hover_primary margin_bottom"
                onClick={() => {
                  on_popup_editor(selected_feature);
                  handleMenuClose();
                }}
              >
                Tampilkan editor atribut
              </button>
              <br />
            </>
          )}
          {!isViewer && (
            <button
              className="button hover_danger"
              onClick={() => {
                setModalDeleteFeature(true);
                handleMenuClose();
              }}
            >
              Delete Feature
            </button>
          )}
        </div>
      </FLOAT_MENU>

      {global_cell_editor && (
        <React.Fragment>
          {(global_cell_editor.type === "selection" ||
            global_cell_editor.type === "radio_button") && (
            <C_E_SELECT
              editorState={global_cell_editor}
              onSave={handleSaveData}
              closeEditor={closeGlobalEditor}
            />
          )}
          {global_cell_editor.type === "checklist" && (
            <C_E_MULTI
              editorState={global_cell_editor}
              onSave={handleSaveData}
              closeEditor={closeGlobalEditor}
            />
          )}
          {global_cell_editor.type === "document" && (
            <C_E_DOC
              editorState={global_cell_editor}
              closeEditor={closeGlobalEditor}
              language={language}
              onSave={handleSaveData}
            />
          )}
          {global_cell_editor.type === "image" && (
            <C_U_IMG
              editorState={global_cell_editor}
              closeEditor={closeGlobalEditor}
              language={language}
              onSave={handleSaveData}
            />
          )}
        </React.Fragment>
      )}

      {modalDeleteFeature && (
        <Modal
          modalSize="small"
          id="modal"
          isOpen={modalDeleteFeature}
          onClose={() => setModalDeleteFeature(false)}
        >
          <div className="box-body" id="box-body">
            <main style={{ textAlign: "center", padding: 20 }}>
              <section className="text_medium">
                {dict?.["Are you sure to delete this feature?"]?.[language] ||
                  "Are you sure to delete this feature?"}
                <br />
                {dict?.["This process can't be undone."]?.[language] ||
                  "This process can't be undone."}
              </section>
              <button
                className="button background_black"
                id="deleteBright"
                style={{ marginTop: "1rem" }}
                onClick={handleDeleteFeature}
              >
                {dict?.["Delete"]?.[language] || "Delete"}
              </button>
            </main>
          </div>
        </Modal>
      )}

      {cityModalConfig &&
        (() => {
          const liveRow =
            data.find((r) => r.id === cityModalConfig.row.id) ||
            fullData.find((r) => r.id === cityModalConfig.row.id);
          const liveValue = liveRow?.properties?.[cityModalConfig.field.key];
          return (
            <C_E_CITY
              config={cityModalConfig}
              liveValue={liveValue}
              onClose={() => setCityModalConfig(null)}
              onSave={handleSaveData}
            />
          );
        })()}

      {addressModalConfig &&
        (() => {
          const liveRow =
            data.find((r) => r.id === addressModalConfig.row.id) ||
            fullData.find((r) => r.id === addressModalConfig.row.id);
          const liveValue = liveRow?.properties?.[addressModalConfig.field.key];
          return (
            <C_E_ADDRESS
              config={addressModalConfig}
              liveValue={liveValue}
              onClose={() => setAddressModalConfig(null)}
              onSave={handleSaveData}
            />
          );
        })()}

      {userModalConfig && (
        <C_E_USER
          config={userModalConfig}
          onClose={() => setUserModalConfig(null)}
        />
      )}

      {latLngModalConfig && (
        <C_E_LATLNG
          config={latLngModalConfig}
          onClose={() => setLatLngModalConfig(null)}
          onSave={handleSaveData}
          language={language}
        />
      )}

      {tableModalConfig && (
        <C_E_TABLE
          config={tableModalConfig}
          onClose={() => {
            setTableModalConfig(null);
            setTimeout(() => {
              cellRefs.current[selectedRow.row]?.[selectedRow.col]?.focus({
                preventScroll: true,
              });
            }, 0);
          }}
        />
      )}
    </>
  );
}
